import axiosInstance from "./axios";
import { errorToast } from "../shared/Toast";

const EMPTY_LIST = { items: [], total: 0, page: 1, limit: 25, totalPages: 0 };

const readFail = (error, fallback, message) => {
  errorToast(error?.response?.data?.message ?? error?.message ?? message);
  return fallback;
};

const writeFail = (error, message) => {
  errorToast(error?.response?.data?.message ?? error?.message ?? message);
  return null;
};

/**
 * Build the multipart body for create/update.
 * @param {object} fields  { merchantId?, appId?, name, bannerType, titleEn?, bodyEn?, titleAr?,
 *                           bodyAr?, htmlEn?, htmlAr?, imageWidth?, imageHeight?, cta?,
 *                           activeFrom?, activeTo?, frequency, frequencyDays?, sortOrder,
 *                           isActive, dismissible, removeImage?, bumpRevision? }
 * @param {File|null} imageFile
 */
export const toBannerFormData = (fields, imageFile) => {
  const fd = new FormData();
  fd.append("dataType", "banner"); // must precede the file
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined) return;
    if (key === "cta") {
      fd.append(key, value === null ? "" : JSON.stringify(value));
      return;
    }
    fd.append(key, value === null ? "" : String(value));
  });
  if (imageFile instanceof File) fd.append("image1", imageFile);
  return fd;
};

// GET /api/app-banners/apps?merchantId → { linked, apps, errorCode?, message? }
export const getBannerApps = async (merchantId) => {
  const empty = { linked: false, apps: [] };
  try {
    const res = await axiosInstance.get("/api/app-banners/apps", { params: { merchantId } });
    if (res?.data && !res.data.isError) return res.data.data || empty;
    errorToast(res?.data?.message ?? "Error loading target apps");
    return empty;
  } catch (error) {
    return readFail(error, empty, "Error loading target apps");
  }
};

// GET /api/app-banners?merchantId&status&search&page&limit
export const listBanners = async (params = {}) => {
  try {
    const res = await axiosInstance.get("/api/app-banners", { params });
    if (res?.data && !res.data.isError) return res.data.data || EMPTY_LIST;
    errorToast(res?.data?.message ?? "Error loading banners");
    return EMPTY_LIST;
  } catch (error) {
    return readFail(error, EMPTY_LIST, "Error loading banners");
  }
};

// GET /api/app-banners/:id
export const getBanner = async (id) => {
  try {
    const res = await axiosInstance.get(`/api/app-banners/${id}`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error loading banner");
    return null;
  } catch (error) {
    return readFail(error, null, "Error loading banner");
  }
};

// POST /api/app-banners (multipart)
export const createBanner = async (fields, imageFile) => {
  try {
    const res = await axiosInstance.post("/api/app-banners", toBannerFormData(fields, imageFile), { rawRequest: true });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error saving banner");
    return null;
  } catch (error) {
    return writeFail(error, "Error saving banner");
  }
};

// PUT /api/app-banners/:id (multipart)
export const updateBanner = async (id, fields, imageFile) => {
  try {
    const res = await axiosInstance.put(`/api/app-banners/${id}`, toBannerFormData(fields, imageFile), { rawRequest: true });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error saving banner");
    return null;
  } catch (error) {
    return writeFail(error, "Error saving banner");
  }
};

// POST /api/app-banners/:id/toggle { isActive } — this is the "delete", and it is reversible.
export const toggleBanner = async (id, isActive) => {
  try {
    const res = await axiosInstance.post(`/api/app-banners/${id}/toggle`, { isActive });
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error switching the banner");
    return null;
  } catch (error) {
    return writeFail(error, "Error switching the banner");
  }
};

// POST /api/app-banners/:id/duplicate → the new copy, switched off
export const duplicateBanner = async (id) => {
  try {
    const res = await axiosInstance.post(`/api/app-banners/${id}/duplicate`);
    if (res?.data && !res.data.isError) return res.data.data;
    errorToast(res?.data?.message ?? "Error duplicating banner");
    return null;
  } catch (error) {
    return writeFail(error, "Error duplicating banner");
  }
};
